<template>
  <v-app>
    <v-main>
      <logout class="noprint" />
      <v-container v-if="loading" fill-height grid-list-md text-center ghost>
        <v-row>
          <v-col>
            <iol-progress-bar />
          </v-col>
        </v-row>
      </v-container>
      <v-container ghost v-else>
        <iol-result-header :fields="results_header" />
        <v-sheet :class="side ? 'iol-right-v-sheet' : 'iol-left-v-sheet'">
          <h2 :align="side ? 'left' : 'right'">
            {{ side ? 'OD' : 'OS' }}
          </h2>
          <v-row>
            <v-col class="col-12 col-md-6" :order-sm="side ? '1' : '2'">
              <iol-result-used-param :fields="common_inputs" />
            </v-col>
            <v-col class="col-12 col-md-6" :order-sm="side ? '2' : '1'">
              <iol-result-switch-history
                v-if="['complex', 'second'].includes(glastCalc)"
                :fields="switch_hist"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="col-12 col-md-6" :order-sm="side ? '1' : '2'">
              <iol-result-corn-ref-surg
                v-if="['complex', 'second'].includes(glastCalc)"
                :fields="corneal_history"
              />
            </v-col>
            <v-col class="col-12 col-md-6" :order-sm="side ? '2' : '1'">
              <iol-result-used-param
                v-if="glastCalc === 'second'"
                :fields="common_inputs_cont"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-row>
                <v-col class="col-12 col-md-6" :order-sm="side ? '2' : '1'">
                  <iol-selected-power-refract
                    :fields="selected_power_refract"
                  />
                  <iol-result-toric-table :fields="results_toric_table" />
                </v-col>
                <v-col class="col-12 col-md-6" :order-sm="side ? '1' : '2'">
                  <canvas
                    width="512"
                    height="384"
                    ref="toricCanvas"
                    class="ioldiag"
                  ></canvas>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row
            v-if="!showText"
            class="align-center justify-center justify-md-space-around"
          >
            <span class="iol-text-diag iol-text-diag-1"
              >IOL axis: {{ results_toric.info.implantation_axis }}°</span
            >
            <span class="iol-text-diag iol-text-diag-2"
              >for SIA {{ results_toric.info.sia }} D & incision &#64;{{
                results_toric.info.sia_axis
              }}°</span
            >
            <span class="iol-text-diag iol-text-diag-2"
              >(Postop. SE : {{ results_toric.info.se }} D for IOL Power SE
              {{ results_toric.info.sign
              }}{{ results_toric.info.power }} D)</span
            >
          </v-row>
        </v-sheet>
        <div class="noprint">
          <v-row>
            <v-col cols="12" class="text-center gray">
              <a @click="print" :style="resetstyle">Print</a>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { mapState } from 'vuex'

import Logout from '@/vue/Logout.vue'

import IolProgressBar from '@/components/elementary/IolProgressBar.vue'
import IolTextField from '@/components/elementary/IolTextField.vue'
import IolSwitchField from '@/components/elementary/IolSwitchField.vue'
import IolResultHeader from '@/components/results/IolResultHeader.vue'
import IolResultUsedParam from '@/components/results/IolResultUsedParam.vue'
import IolResultToricTable from '@/components/results/IolResultToricTable.vue'
import IolResultCornRefSurg from '@/components/results/IolResultCornRefSurg.vue'
import IolSelectedPowerRefract from '@/components/results/IolSelectedPowerRefract.vue'
import IolResultSwitchHistory from '@/components/results/IolResultSwitchHistory.vue'

export default {
  beforeDestroy() {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener)
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('updateToricCalcAllowed', false)
    next()
  },
  name: 'ResultsToric',
  components: {
    Logout,
    IolProgressBar,
    IolResultHeader,
    IolTextField,
    IolResultUsedParam,
    IolResultToricTable,
    IolSwitchField,
    IolResultCornRefSurg,
    IolSelectedPowerRefract,
    IolResultSwitchHistory,
  },
  watch: {
    loading(newVal) {
      if (!newVal) {
        this.$nextTick(() => {
          this.drawCanvas()
        })
      }
    },
  },
  mounted() {
    setTimeout(() => {
      this.loading = false
    }, 2000)

    this.resizeListener = () => {
      if (this.$refs.toricCanvas && !this.loading) {
        this.drawCanvas(this.showText)
      }
    }
    window.addEventListener('resize', this.resizeListener)

    // init patient/kera/biom
    this.results_header.patientID.text = this.ginputs.patientID
    this.results_header.keratometric_index.text = this.ginputs.keratometric_index
    this.results_header.biometer.text = this.ginputs.biometer.text
    this.results_header.title = `${this.results_header.title} (from ${
      this.glastCalc.split('_')[0]
    })`

    // init common inputs
    this.common_inputs.al = this.ginputs.al
    this.common_inputs.k1 = this.ginputs.k1
    this.common_inputs.k2 = this.ginputs.k2
    this.common_inputs.acd = this.ginputs.acd
    this.common_inputs.aconst = this.ginputs.aconst
    this.common_inputs.target = this.ginputs.target
    this.common_inputs.lt = this.ginputs.lt
    this.common_inputs.cct = this.ginputs.cct
    this.common_inputs.inputs_type = this.glastCalc
    this.side = this.ginputs.side
    if (this.glastCalc === 'regular_os') {
      this.common_inputs.al = this.ginputs2.al
      this.common_inputs.k1 = this.ginputs2.k1
      this.common_inputs.k2 = this.ginputs2.k2
      this.common_inputs.acd = this.ginputs2.acd
      this.common_inputs.aconst = this.ginputs2.aconst
      this.common_inputs.target = this.ginputs2.target
      this.common_inputs.lt = this.ginputs2.lt
      this.common_inputs.cct = this.ginputs2.cct
      this.side = false //this.ginputs2.side TODO: c'est naze y'a un conflit entre side et OD/OS ?
    } else if (this.glastCalc === 'complex') {
      if (this.ginputs.prc_topo) {
        this.common_inputs.total_corneal_power_measurement = this.ginputs.prc_topo
        this.common_inputs.total_corneal_power_measurement_title = 'PRC_topo'
        this.common_inputs.total_corneal_power_measurement_unit = 'mm'
      } else if (this.ginputs.tk) {
        this.common_inputs.total_corneal_power_measurement = this.ginputs.tk
        this.common_inputs.total_corneal_power_measurement_title = 'TK'
        this.common_inputs.total_corneal_power_measurement_unit = 'D'
      } else if (this.ginputs.tcrp) {
        this.common_inputs.total_corneal_power_measurement = this.ginputs.tcrp
        this.common_inputs.total_corneal_power_measurement_title = 'TCRP'
        this.common_inputs.total_corneal_power_measurement_unit = 'D'
      }
    } else if (this.glastCalc === 'second') {
      // init common inputs controlateral
      this.common_inputs_cont.al = this.ginputs.al2
      this.common_inputs_cont.k1 = this.ginputs.k12
      this.common_inputs_cont.k2 = this.ginputs.k22
      this.common_inputs_cont.acd = this.ginputs.acd2
      this.common_inputs_cont.aconst = this.ginputs.aconst
      this.common_inputs_cont.lt = this.ginputs.lt2
      this.common_inputs_cont.cct = this.ginputs.cct2
      this.common_inputs_cont.se2 = this.ginputs.se2
      this.common_inputs_cont.power2 = this.ginputs.power2
    }

    // init selected power/refractation
    this.selected_power_refract.power = this.ginputs_toric.selected_power
    this.selected_power_refract.es = this.ginputs_toric.selected_es

    // init icl/rk/non_physio_cornea
    this.switch_hist.is_icl = this.ginputs.is_icl
    this.switch_hist.is_rk = this.ginputs.is_rk
    this.switch_hist.is_non_physio_cornea = this.ginputs.is_non_physio_cornea

    // init corneal history
    if (['complex', 'second'].includes(this.glastCalc)) {
      this.corneal_history.is_corneal_laser = this.ginputs.is_corneal_laser
      this.corneal_history.corneal_laser_type = this.ginputs.corneal_laser_type.text
      this.corneal_history.pre_lvc_arc = this.ginputs.pre_lvc_arc
      this.corneal_history.laser_amount = this.ginputs.laser_amount
    }

    // init result
    this.results_toric_table.pm1 = this.results_toric.pm1
    this.results_toric_table.pm05 = this.results_toric.pm05
    this.results_toric_table.p0 = this.results_toric.p0
    this.results_toric_table.p05 = this.results_toric.p05
    this.results_toric_table.p1 = this.results_toric.p1
  },
  data: () => {
    return {
      resetstyle: { color: '#838383' },
      loading: true,
      showText: true,
      results_header: {
        title: 'PEARL-DGS - Toric Calculation Results',
        patientID: {
          label: 'Patient ID / Name',
          readonly: true,
          tooltip: '',
          text: '',
        },
        keratometric_index: {
          label: 'Keratometric Index',
          readonly: true,
          tooltip: '',
          text: '',
        },
        biometer: {
          label: 'biometer',
          readonly: true,
          tooltip: '',
          text: '',
        },
      },
      common_inputs: {
        al: '',
        k1: '',
        k2: '',
        acd: '',
        aconst_title: 'A Constant',
        aconst: '',
        target_title: 'Target refraction',
        target: '',
        power2_title: 'Implanted IOL power',
        power2: '',
        lt: '',
        cct: '',
      },
      common_inputs_cont: {
        al: '',
        k1: '',
        k2: '',
        acd: '',
        aconst_title: 'Implanted A constant',
        aconst: '',
        target_title: 'Target refraction',
        target: '',
        lt: '',
        cct: '',
        se2_title: 'Postop. spherical equivalent',
        se2: '',
        power2_title: 'Implanted IOL power',
        power2: '',
        inputs_type: 'controlateral',
        is_outlined: true,
      },
      side: true,
      switch_hist: {
        is_rk: false,
        is_icl: false,
        is_non_physio_cornea: false,
      },
      corneal_history: {
        is_corneal_laser: false,
        corneal_laser_type: '',
        pre_lvc_arc: '',
        laser_amount: '',
      },
      selected_power_refract: {
        th1: 'IOL POWER (D)',
        th2: 'REFRACTION (D)',
        power: '',
        es: '',
      },
      results_toric_table: {
        th1: 'IOL CYLINDER (D)',
        th2: 'RESIDUAL REF. CYL (D)',
        th3: 'RESIDUAL AXIS (°)',
        pm1: {
          tor: '',
          cyl: '',
          axis: '',
        },
        pm05: {
          tor: '',
          cyl: '',
          axis: '',
        },
        p0: {
          tor: '',
          cyl: '',
          axis: '',
        },
        p05: {
          tor: '',
          cyl: '',
          axis: '',
        },
        p1: {
          tor: '',
          cyl: '',
          axis: '',
        },
        footer: '',
      },
    }
  },
  computed: {
    ...mapState([
      'results_toric',
      'ginputs',
      'ginputs2',
      'ginputs_toric',
      'appHeader',
      'glastCalc',
    ]),
  },
  methods: {
    print() {
      window.print()
    },
    drawCanvas(showText = true) {
      const canvas = this.$refs.toricCanvas
      if (!canvas) {
        console.error('Canvas ref not found')
        return
      }

      // Make canvas responsive
      this.resizeCanvas(canvas, showText)

      const ctx = canvas.getContext('2d')
      this.vueCanvas = ctx

      this.drawToricGraph(
        canvas,
        this.results_toric.info.se,
        this.results_toric.info.power,
        this.results_toric.info.side,
        this.results_toric.info.implantation_axis,
        this.results_toric.info.sia,
        this.results_toric.info.sia_axis,
        showText,
      )
    },

    resizeCanvas(canvas, showText = false) {
      const container = canvas.parentElement
      const containerRect = container.getBoundingClientRect()

      // Set canvas size to container size with devicePixelRatio for crisp rendering
      const dpr = window.devicePixelRatio || 1
      const displayWidth = containerRect.width

      // Calculate height based on content needs - minimal margins
      let displayHeight
      if (showText) {
        // With text: minimal space for graph + bottom text
        displayHeight = displayWidth * 0.7 // Reduced to minimize bottom margin
      } else {
        // Without text: tight fit around the graph with degree labels
        displayHeight = displayWidth * 0.55 // Further reduced to eliminate bottom margin
      }

      canvas.width = displayWidth * dpr
      canvas.height = displayHeight * dpr
      canvas.style.width = displayWidth + 'px'
      canvas.style.height = displayHeight + 'px'

      // Scale context to account for device pixel ratio
      const ctx = canvas.getContext('2d')
      ctx.scale(dpr, dpr)
    },

    drawToricGraph(
      canvas,
      se,
      power,
      side,
      implantation_axis,
      sia,
      sia_axis,
      showText = false,
    ) {
      // Clear canvas
      this.vueCanvas.clearRect(0, 0, canvas.width, canvas.height)

      // Save the context state
      this.vueCanvas.save()

      // Calculate responsive dimensions
      const displayWidth = canvas.clientWidth
      const displayHeight = canvas.clientHeight
      const minDimension = Math.min(displayWidth, displayHeight)

      // Center positioning - adjusted based on whether text is shown
      const centerX = displayWidth / 2
      const centerY = showText ? displayHeight * 0.38 : displayHeight * 0.5
      this.vueCanvas.translate(centerX, centerY)

      // Responsive scale factor - double size when text is hidden
      const scale = showText ? minDimension * 0.26 : minDimension * 0.38

      // Responsive line widths and font sizes
      const baseSize = minDimension / 400
      const mainLineWidth = Math.max(2, 4 * baseSize)
      const innerLineWidth = Math.max(1, 2 * baseSize)
      const arcLineWidth = Math.max(3, 8 * baseSize)
      const fontSize = Math.max(10, 18 * baseSize)
      const smallFontSize = Math.max(8, 14 * baseSize)

      // Draw main black circle
      this.vueCanvas.strokeStyle = 'black'
      this.vueCanvas.lineWidth = mainLineWidth
      this.vueCanvas.beginPath()
      this.vueCanvas.arc(0, 0, scale, 0, 2 * Math.PI)
      this.vueCanvas.stroke()

      // Draw degree markers and labels
      this.vueCanvas.fillStyle = 'black'
      this.vueCanvas.strokeStyle = 'black'
      this.vueCanvas.lineWidth = Math.max(1, baseSize)
      this.vueCanvas.font = `${fontSize}px Arial`
      this.vueCanvas.textAlign = 'center'
      this.vueCanvas.textBaseline = 'middle'

      for (let angle = 0; angle < 360; angle += 45) {
        const rad = (angle * Math.PI) / 180
        const x = Math.cos(rad)
        const y = Math.sin(rad)

        // Draw tick marks
        this.vueCanvas.beginPath()
        this.vueCanvas.moveTo(x * scale * 0.9, -y * scale * 0.9)
        this.vueCanvas.lineTo(x * scale, -y * scale)
        this.vueCanvas.stroke()

        // Draw angle labels
        this.vueCanvas.fillText(`${angle}°`, x * scale * 1.2, -y * scale * 1.2)
      }

      // Draw red arc (SIA indicator)
      // Convert from medical convention (counter-clockwise from right) to canvas convention (clockwise from top)
      const sia_axis_plot = (90 - sia_axis) % 360
      this.vueCanvas.strokeStyle = 'red'
      this.vueCanvas.lineWidth = arcLineWidth
      this.vueCanvas.beginPath()
      const arcStart = ((sia_axis_plot - 10 - 90) * Math.PI) / 180
      const arcEnd = ((sia_axis_plot + 10 - 90) * Math.PI) / 180
      this.vueCanvas.arc(0, 0, scale * 1.025, arcStart, arcEnd)
      this.vueCanvas.stroke()

      // Draw inner circle with blue fill
      const innerRadius = scale * 0.75
      this.vueCanvas.strokeStyle = 'red'
      this.vueCanvas.fillStyle = '#97d9fc'
      this.vueCanvas.lineWidth = innerLineWidth
      this.vueCanvas.setLineDash([5 * baseSize, 5 * baseSize])
      this.vueCanvas.beginPath()
      this.vueCanvas.arc(0, 0, innerRadius, 0, 2 * Math.PI)
      this.vueCanvas.fill()
      this.vueCanvas.stroke()
      this.vueCanvas.setLineDash([])

      // Draw dashed red line (IOL axis)
      // Convert from medical convention (counter-clockwise from right) to canvas convention (clockwise from top)
      const implantation_axis_plot = (90 - implantation_axis) % 360
      const axisRad = ((implantation_axis_plot - 90) * Math.PI) / 180
      const xMid = Math.cos(axisRad) * innerRadius * 0.49
      const yMid = Math.sin(axisRad) * innerRadius * 0.49
      const xEnd = Math.cos(axisRad) * innerRadius * 0.83
      const yEnd = Math.sin(axisRad) * innerRadius * 0.83

      this.vueCanvas.strokeStyle = 'red'
      this.vueCanvas.lineWidth = innerLineWidth
      this.vueCanvas.setLineDash([5 * baseSize, 5 * baseSize])
      this.vueCanvas.beginPath()
      this.vueCanvas.moveTo(-xEnd, -yEnd)
      this.vueCanvas.lineTo(-xMid, -yMid)
      this.vueCanvas.moveTo(xMid, yMid)
      this.vueCanvas.lineTo(xEnd, yEnd)
      this.vueCanvas.stroke()
      this.vueCanvas.setLineDash([])

      // Draw text only if showText is true
      if (showText) {
        // Draw eye side text (OD top left corner, OS top right corner of canvas)
        this.vueCanvas.fillStyle = 'black'
        this.vueCanvas.font = `bold ${fontSize}px Arial`
        this.vueCanvas.textAlign = 'left'
        this.vueCanvas.textBaseline = 'top'

        // Reset translation to draw at canvas corners
        this.vueCanvas.save()
        this.vueCanvas.setTransform(1, 0, 0, 1, 0, 0)

        if (side) {
          // OD (right eye) - display on top left corner
          this.vueCanvas.fillText('OD', 10, 10)
        } else {
          // OS (left eye) - display on top right corner
          this.vueCanvas.textAlign = 'right'
          this.vueCanvas.fillText('OS', displayWidth - 10, 10)
        }

        this.vueCanvas.restore()

        // Draw text information below the graph (smaller font)
        this.vueCanvas.fillStyle = 'black'
        this.vueCanvas.textAlign = 'center'
        this.vueCanvas.font = `bold ${smallFontSize}px Arial`
        this.vueCanvas.fillText(
          `IOL axis: ${implantation_axis}°`,
          0,
          scale * 1.5,
        )

        this.vueCanvas.font = `${smallFontSize}px Arial`
        this.vueCanvas.fillText(
          `for SIA ${sia} D & incision @${sia_axis}°`,
          0,
          scale * 1.8,
        )

        const sign = power > 0 ? '+' : ''
        this.vueCanvas.fillText(
          `(Postop. SE : ${se} D for IOL Power SE ${sign}${power} D)`,
          0,
          scale * 2.1,
        )
      }

      // Restore the context state
      this.vueCanvas.restore()
    },
  },
}
</script>
<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.ioldiag::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.ioldiag {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  width: 100%;
  height: auto;
  display: block;
  max-width: 100%;
  /* border: 1px solid #ddd; */
}

.iol-text-diag {
  font-family: inherit;
}
.iol-text-diag-1 {
  font-size: 1.8rem;
  font-weight: 600;
}
.iol-text-diag-2 {
  font-size: 1rem;
  font-weight: 100;
}
</style>
